import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ExternalLink,
  Star,
  UserPlus,
  ListChecks,
  X,
  Radar as RadarIcon,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import type { FreelancerSugerido } from "./useVagaAssistant";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";

import { DisponibilidadeLinha } from "@/components/freelancers/DisponibilidadeBadge";
import { useDisponibilidade } from "@/hooks/useDisponibilidade";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";

interface FreelancerSuggestionCardProps {
  freelancer: FreelancerSugerido;
  onSelecionados?: (freelancerId: string) => void;
  onShortlist?: (freelancerId: string) => void;
  onDescartar?: (freelancer: FreelancerSugerido) => void;
  isProcessing?: boolean;
}

/** Iniciais (até 2) do nome, para o avatar quando não há foto. */
function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function FreelancerSuggestionCard({
  freelancer,
  onSelecionados,
  onShortlist,
  onDescartar,
  isProcessing,
}: FreelancerSuggestionCardProps) {
  const { data: disponibilidade } = useDisponibilidade(freelancer.id);
  // Score exibido: prioriza o match da vaga (motor) e cai pro composto.
  const score = freelancer.overall_score ?? freelancer.score_composto;
  return (
    <TooltipProvider delayDuration={300}>
    <Card className="border border-border/50 bg-card/50">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            {freelancer.foto_url ? (
              <img
                src={freelancer.foto_url}
                alt={freelancer.nome}
                className="h-9 w-9 rounded-full object-cover shrink-0 border border-border/50"
              />
            ) : (
              <div className="h-9 w-9 rounded-full shrink-0 bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-semibold">
                {getInitials(freelancer.nome)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <a
                href={`/freelancers/${freelancer.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackFreelancerView(freelancer.id, "assistente_ia")}
                className="font-medium text-sm truncate hover:text-purple-600 hover:underline block"
              >
                {freelancer.nome}
              </a>
              <DisponibilidadeLinha summary={disponibilidade} level="inline" className="mt-0.5" />
              {freelancer.cidade && (
                <p className="text-xs text-muted-foreground">{freelancer.cidade}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {freelancer.radar_indicado && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="gap-1 text-xs bg-amber-50 text-amber-800 border-amber-300"
                  >
                    <RadarIcon className="h-3 w-3" />
                    Indicado no radar
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Marcado manualmente como indicado para esta agência no Radar de Perfis
                </TooltipContent>
              </Tooltip>
            )}
            {score != null && (
              <Badge variant="secondary" className="text-xs shrink-0">
                {score.toFixed(0)}
              </Badge>
            )}
          </div>
        </div>

        {/* Áreas */}
        {freelancer.areas && freelancer.areas.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {freelancer.areas.slice(0, 3).map((area) => (
              <Badge key={area} variant="outline" className="text-[10px] px-1.5 py-0">
                {area}
              </Badge>
            ))}
          </div>
        )}

        {/* Scores */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {freelancer.media_perfil != null && (
            <span className="flex items-center gap-0.5">
              <Star className="h-3 w-3" />
              Perfil {freelancer.media_perfil.toFixed(1)}
            </span>
          )}
          {freelancer.media_projeto != null && (
            <span>Projeto {freelancer.media_projeto.toFixed(1)}</span>
          )}
          {freelancer.similarity != null && (
            <span>Match {(freelancer.similarity * 100).toFixed(0)}%</span>
          )}
        </div>

        {/* Pretensão */}
        {freelancer.pretensao_salarial && (
          <p className="text-xs text-muted-foreground">
            R$ {freelancer.pretensao_salarial}
          </p>
        )}

        {/* Disponibilidade fica só no DisponibilidadeBadge unificado (linha do
            nome), que lê v_availability_summary — a mesma verdade do assistente.
            A marca manual de status já entra nessa efetiva (sync pela M142), então
            o badge manual redundante foi removido. */}

        {/* Notas de Avaliação */}
        {freelancer.notas_avaliacao && Object.keys(freelancer.notas_avaliacao).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {Object.entries(freelancer.notas_avaliacao).flatMap(([, notas]) =>
              Object.entries(notas as Record<string, number>).map(([item, valor]) => (
                <Badge
                  key={item}
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 ${
                    valor >= 8 ? "bg-green-50 text-green-700 border-green-200"
                    : valor >= 6 ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                    : "bg-gray-50 text-gray-600 border-gray-200"
                  }`}
                >
                  {item}: {valor}
                </Badge>
              ))
            )}
          </div>
        )}

        {/* Style Tags */}
        {freelancer.style_tags && freelancer.style_tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {freelancer.style_tags.slice(0, 3).map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Pontos fortes / Ressalvas (vêm prontos do motor de match) */}
        {freelancer.pontos_fortes && freelancer.pontos_fortes.length > 0 && (
          <div className="space-y-0.5">
            {freelancer.pontos_fortes.slice(0, 3).map((p, i) => (
              <div key={i} className="flex gap-1 text-[11px] text-green-700">
                <span className="shrink-0">✓</span>
                <span>{p}</span>
              </div>
            ))}
          </div>
        )}
        {freelancer.ressalvas && freelancer.ressalvas.length > 0 && (
          <div className="space-y-0.5">
            {freelancer.ressalvas.slice(0, 2).map((r, i) => (
              <div key={i} className="flex gap-1 text-[11px] text-amber-700">
                <span className="shrink-0">!</span>
                <span>{r}</span>
              </div>
            ))}
          </div>
        )}

        {/* Ações */}
        <div className="flex items-center gap-1.5 pt-1">
          <ExternalLinkButton
            kind="portfolio"
            url={freelancer.portfolio_url}
            icon={ExternalLink}
            className="h-7 w-auto px-2"
            iconClassName="h-3 w-3"
          />
          <FreelancerLinkedInButton
            freelancerId={freelancer.id}
            url={freelancer.linkedin_url}
            className="h-7 w-auto px-2"
            iconClassName="h-3 w-3"
          />
          <div className="flex items-center gap-1 ml-auto">
            {onSelecionados && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs px-2"
                    onClick={() => {
                      trackFreelancerView(freelancer.id, "assistente_ia");
                      onSelecionados(freelancer.id);
                    }}
                    disabled={isProcessing}
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Selecionar
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Adicionar em Selecionados</TooltipContent>
              </Tooltip>
            )}
            {onShortlist && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs px-2"
                    onClick={() => {
                      trackFreelancerView(freelancer.id, "assistente_ia");
                      onShortlist(freelancer.id);
                    }}
                    disabled={isProcessing}
                  >
                    <ListChecks className="h-3 w-3 mr-1" />
                    Shortlist
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Adicionar na Shortlist</TooltipContent>
              </Tooltip>
            )}
            {onDescartar && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs px-2 text-destructive hover:text-destructive"
                    onClick={() => onDescartar(freelancer)}
                    disabled={isProcessing}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Descartar</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
